import {
  CareServiceOrgansKey,
  ConditionDependency,
  ConditionDependencyDetail,
  ReportOrgan,
  TextToStructuredObservation,
} from '@/api';
import { DEGENERATIVE_CHANGE_CONDITION, ENDOMETRIAL_THICKNESS_CONDITION } from '@/shared/constant';
import { FindingsByOrgan, ReportLink, ReportLinkKey } from '@/shared/model';
import { isEmpty, isNotEmpty, isUterusAppearanceAbsentOrPartial } from '@/shared/util';
import { ReportValidationStatus } from '@/store';

import { isFindingMissingPriorScanComparison } from '../organ/organ.util';
import { DEFAULT_QUESTIONS } from '../shared/constant';
import { MissingField } from '../shared/model';
import {
  evaluateConditionShowWhen,
  evaluateMissingFields,
  evaluateRequiredWhen,
  isOrganDisabled as isOrganDisabledUtil,
} from '../shared/util';

function evaluateMissingQuestions(
  key: string,
  organData: TextToStructuredObservation[],
  result: MissingField,
) {
  Object.entries(DEFAULT_QUESTIONS?.[key]).forEach(([question, questionData]) => {
    const organFindings = organData;
    const requiredWhen = questionData?.requiredWhen;
    let requiredQuestions;

    if (requiredWhen) {
      requiredQuestions = evaluateRequiredWhen(requiredWhen, organFindings);
    } else {
      requiredQuestions = questionData?.required;
    }

    if (requiredQuestions) {
      const hasMissingRequired = !organFindings.some(
        (finding) => finding.conditionTemplate.condition === question,
      );

      if (hasMissingRequired) {
        Object.assign(result, {
          ...result,
          [key]: { error: true, findingIndex: questionData.order, observationIndex: 0 },
        });
      }
    }
  });
}

export function evaluateGroupedFindingsForMissingFields(
  groupedData: FindingsByOrgan,
  links: ReportLink[],
  reportOrgans: Record<string, ReportOrgan>,
): MissingField {
  return Object.entries(groupedData).reduce((result, [key, organData]) => {
    const organStatusDisabled = isOrganDisabledUtil(key, links);

    if (DEFAULT_QUESTIONS?.[key] && !organStatusDisabled && !reportOrgans[key]?.incompleteScan) {
      evaluateMissingQuestions(key, organData, result);
    }

    organData.forEach((finding, findingIndex) => {
      if (
        finding.conditionTemplate.condition?.startsWith('default_question_') &&
        (organStatusDisabled || reportOrgans[key]?.incompleteScan)
      ) {
        return;
      }

      evaluateMissingFields(finding, result, key, findingIndex, organData);
    });

    return result;
  }, {} as MissingField);
}

export function getReportOrgans(
  reportOrgan: ReportOrgan | ReportOrgan[] | undefined,
  reportUuid: string,
): null | Record<string, Record<string, ReportOrgan>> {
  if (!reportOrgan) {
    return null;
  }

  if (Array.isArray(reportOrgan)) {
    return {
      [reportUuid]: reportOrgan.reduce(
        (acc: { [key: string]: ReportOrgan }, organ: ReportOrgan) => {
          acc[organ.organ] = organ;

          return acc;
        },
        {} as { [key: string]: ReportOrgan },
      ),
    };
  }

  return { [reportUuid]: { [reportOrgan.organ]: reportOrgan } };
}

export function hasInvalidScore(finding: TextToStructuredObservation): boolean {
  return [finding.renderedSummary?.csdScore, finding.renderedSummary?.oncoradsScore].some(
    (score) => score === null || score === undefined || score === 0,
  );
}

export function hasFindingsWithInvalidScores(
  findings: FindingsByOrgan,
  links: ReportLink[],
): {
  hasInvalidScores: boolean;
  organsWithInvalidScores: ReportLinkKey[];
} {
  const organsWithInvalidScores = Object.entries(findings)
    .filter(([organKey]) => !isOrganDisabledUtil(organKey, links))
    .filter(([, organFindings]) =>
      organFindings
        .filter(
          (finding) =>
            finding?.conditionTemplate?.condition &&
            !finding.conditionTemplate.condition.startsWith('default_question_') &&
            finding.conditionTemplate.condition !== DEGENERATIVE_CHANGE_CONDITION,
        )
        .some((finding) => hasInvalidScore(finding)),
    )
    .map(([organKey]) => organKey as ReportLinkKey);

  return {
    hasInvalidScores: isNotEmpty(organsWithInvalidScores),
    organsWithInvalidScores,
  };
}

function isConditionDetailRequired(
  conditionDetail: { required?: boolean; requiredWhen?: unknown },
  organFindings: TextToStructuredObservation[],
): boolean {
  if (conditionDetail.requiredWhen) {
    return evaluateRequiredWhen(
      conditionDetail.requiredWhen as Parameters<typeof evaluateRequiredWhen>[0],
      organFindings,
    );
  }

  return !!conditionDetail.required;
}

function isFieldVisibleForUpfront(
  showWhen: ConditionDependency | null | undefined,
  organFindings: TextToStructuredObservation[],
): boolean {
  if (!showWhen) return true;

  const knownValues: Record<string, unknown> = {};

  for (const finding of organFindings) {
    for (const rf of finding.renderedSummary?.reportFindings ?? []) {
      for (const detail of rf.conditionDetail ?? []) {
        if (detail.name && detail.value !== null && detail.value !== undefined) {
          knownValues[detail.name] = detail.value;
        }
      }
    }
  }

  const result = evaluateConditionShowWhen(showWhen, knownValues, organFindings);

  if (result) return true;

  const { condition } = showWhen;
  const referencedFields = Array.isArray(condition)
    ? condition.map((c) => (c as ConditionDependencyDetail).field).filter(Boolean)
    : [(condition as ConditionDependencyDetail).field].filter(Boolean);

  const hasKnownValue = referencedFields.some(
    (field) => knownValues[field!] !== null && knownValues[field!] !== undefined,
  );

  return !hasKnownValue;
}

function hasDefaultQuestionErrorFromFindings(
  linkKey: string,
  organFindings?: TextToStructuredObservation[],
): boolean {
  if (!DEFAULT_QUESTIONS[linkKey] || isEmpty(organFindings)) {
    return false;
  }

  return organFindings
    .filter((finding) => finding.conditionTemplate.condition?.startsWith('default_question_'))
    .some((finding) => {
      const conditionDetails = finding.conditionTemplate.conditionDetail ?? [];
      const reportFindings = finding.renderedSummary?.reportFindings ?? [];

      if (isEmpty(reportFindings)) {
        return conditionDetails.some(
          (conditionDetail) =>
            isConditionDetailRequired(conditionDetail, organFindings) &&
            isFieldVisibleForUpfront(conditionDetail.showWhen, organFindings),
        );
      }

      return reportFindings.some((reportFinding) => {
        const reportDetails = reportFinding.conditionDetail ?? [];
        const valuesMap = reportDetails.reduce(
          (result, reportDetail) => ({ ...result, [reportDetail.name]: reportDetail.value }),
          {} as Record<string, unknown>,
        );

        return conditionDetails
          .filter((conditionDetail) => {
            if (!isConditionDetailRequired(conditionDetail, organFindings)) return false;

            return (
              !conditionDetail.showWhen ||
              evaluateConditionShowWhen(conditionDetail.showWhen, valuesMap, organFindings)
            );
          })
          .some((conditionDetail) => {
            const matchingDetail = reportDetails.find(
              (reportDetail) => reportDetail.name === conditionDetail.name,
            );
            const value = matchingDetail?.value;

            return isEmpty(value);
          });
      });
    });
}

function hasMissingPriorScanComparisonFromFindings(
  organFindings?: TextToStructuredObservation[],
  validateStatus?: boolean,
): boolean {
  if (isEmpty(organFindings)) {
    return false;
  }

  return organFindings.some((finding) =>
    isFindingMissingPriorScanComparison(finding, { validateStatus }),
  );
}

interface ValidateOrganStatusArgs {
  defaultQuestionStatus?: Record<string, Record<string, boolean>>;
  hasDraftLesion: boolean;
  hasFindings: boolean;
  hasInvalidScores: boolean;
  hasPreviousStudyResults?: boolean;
  isMissing: boolean;
  isOrganDisabled: boolean;
  linkKey: ReportLinkKey;
  organFindings?: TextToStructuredObservation[];
  organs: Record<ReportLinkKey, ReportValidationStatus>;
  setStatus: (link: ReportLinkKey, status: ReportValidationStatus) => void;
  validateComparisonStatus?: boolean;
}

export function validateOrganStatus({
  defaultQuestionStatus,
  hasDraftLesion,
  hasFindings,
  hasInvalidScores,
  hasPreviousStudyResults,
  isMissing,
  isOrganDisabled,
  linkKey,
  organFindings,
  organs,
  setStatus,
  validateComparisonStatus,
}: ValidateOrganStatusArgs): void {
  const status = organs[linkKey] || { done: false, error: false, warning: false };

  const storeStatus = defaultQuestionStatus?.[linkKey];
  const hasDefaultQuestionError =
    storeStatus && isNotEmpty(Object.keys(storeStatus))
      ? Object.entries(storeStatus).some(([key, hasError]) => {
          if (
            key === ENDOMETRIAL_THICKNESS_CONDITION &&
            isUterusAppearanceAbsentOrPartial(organFindings ?? [])
          ) {
            return false;
          }

          return hasError;
        })
      : hasDefaultQuestionErrorFromFindings(linkKey, organFindings);

  const hasMissingComparison =
    !!hasPreviousStudyResults &&
    hasMissingPriorScanComparisonFromFindings(organFindings, validateComparisonStatus);

  const hasError =
    isOrganDisabled && !hasFindings
      ? false
      : isMissing ||
        hasInvalidScores ||
        hasDraftLesion ||
        hasDefaultQuestionError ||
        hasMissingComparison;

  organs[linkKey] = { ...status, error: hasError };
  setStatus(linkKey, organs[linkKey]);
}

interface ValidateOrgansArgs {
  defaultQuestionStatus?: Record<string, Record<string, boolean>>;
  hasPreviousStudyResults?: boolean;
  invalidScoresSet: Set<ReportLinkKey>;
  links: ReportLink[];
  mergedFindings: Record<CareServiceOrgansKey, unknown[]>;
  missingRequiredFields: MissingField;
  organs: Record<ReportLinkKey, ReportValidationStatus>;
  organsWithDraftLesions: Record<ReportLinkKey, boolean>;
  setStatus: (link: ReportLinkKey, status: ReportValidationStatus) => void;
  validateComparisonStatus?: boolean;
}

export function validateOrgans({
  defaultQuestionStatus,
  hasPreviousStudyResults,
  invalidScoresSet,
  links,
  mergedFindings,
  missingRequiredFields,
  organs,
  organsWithDraftLesions,
  setStatus,
  validateComparisonStatus,
}: ValidateOrgansArgs): void {
  links.forEach((link) => {
    const linkKey = link.key as ReportLinkKey;
    const organKey = linkKey as CareServiceOrgansKey;
    const organFindings = mergedFindings[organKey] as TextToStructuredObservation[] | undefined;
    const hasFindings = (organFindings?.length ?? 0) > 0;
    const isMissing = !!missingRequiredFields[organKey];
    const hasInvalidScores = invalidScoresSet.has(linkKey);
    const hasDraftLesion = !!organsWithDraftLesions[linkKey];

    validateOrganStatus({
      defaultQuestionStatus,
      hasDraftLesion,
      hasFindings,
      hasInvalidScores,
      hasPreviousStudyResults,
      isMissing,
      isOrganDisabled: isOrganDisabledUtil(linkKey, links) || false,
      linkKey,
      organFindings,
      organs,
      setStatus,
      validateComparisonStatus,
    });

    link.sub?.forEach((subLink) => {
      const subLinkKey = subLink.key as ReportLinkKey;
      const subOrganKey = subLinkKey as CareServiceOrgansKey;
      const subOrganFindings = mergedFindings[subOrganKey] as
        TextToStructuredObservation[] | undefined;
      const subHasFindings = (subOrganFindings?.length ?? 0) > 0;
      const subIsMissing = !!missingRequiredFields[subOrganKey];
      const subHasInvalidScores = invalidScoresSet.has(subLinkKey);
      const subHasDraftLesion = !!organsWithDraftLesions[subLinkKey];

      validateOrganStatus({
        defaultQuestionStatus,
        hasDraftLesion: subHasDraftLesion,
        hasFindings: subHasFindings,
        hasInvalidScores: subHasInvalidScores,
        hasPreviousStudyResults,
        isMissing: subIsMissing,
        isOrganDisabled: isOrganDisabledUtil(subLinkKey, links) || false,
        linkKey: subLinkKey,
        organFindings: subOrganFindings,
        organs,
        setStatus,
        validateComparisonStatus,
      });
    });
  });
}
